import { useModalStore } from '@/shared/store/modalStore';
import { useShallow } from 'zustand/react/shallow';
import { X } from 'lucide-react';

import { CustomAlertDialog, CustomAlertDialogContent, CustomAlertDialogCancel, CustomAlertDialogAction } from '@/shared/shadcn/custom/alert-dialog';

export default function GlobalAlertDialog() {
  const { isOpen, message, type, noTitle, resolve, close, confirmText, cancelText, inputs, inputValues, inputErrors, setInputValue, setInputError } =
    useModalStore(
      useShallow((state) => ({
        isOpen: state.isOpen,
        message: state.message,
        type: state.type,
        noTitle: state.noTitle,
        resolve: state.resolve,
        close: state.close,
        confirmText: state.confirmText,
        cancelText: state.cancelText,
        inputs: state.inputs,
        inputValues: state.inputValues,
        inputErrors: state.inputErrors,
        setInputValue: state.setInputValue,
        setInputError: state.setInputError,
      }))
    );

  const handleAction = () => {
    if (type === 'prompt') {
      const errors = inputs.map((field, i) => (field.validator ? (field.validator(inputValues[i] ?? '') ?? '') : ''));
      if (errors.some(Boolean)) {
        errors.forEach((err, i) => setInputError(i, err));
        return;
      }
      resolve?.(inputValues);
    } else {
      resolve?.(true);
    }
    close();
  };

  const handleCancel = () => {
    resolve?.(false);
    close();
  };

  const handleAutoCancel = () => {
    resolve?.(false);
    close();
  };

  const title = noTitle ? '' : message.split('\n')[0];
  const description = message.split('\n').slice(1).join('\n');

  return (
    <CustomAlertDialog open={isOpen}>
      <CustomAlertDialogContent className='max-h-fit rounded-xl bg-white p-6' title={title} description={description}>
        <button
          type='button'
          onClick={handleAutoCancel}
          className='absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-sm'
          aria-label='close modal'>
          <X className='h-4 w-4 text-gray-900' />
        </button>

        <div>
          {noTitle ? (
            <p className='text-left whitespace-pre-line text-gray-900'>{message}</p>
          ) : (
            <>
              <h3 className='pr-6 text-xl font-semibold'>{title}</h3>
              <p className='mt-1 text-left whitespace-pre-line text-gray-900'>{description}</p>
            </>
          )}
          {type === 'prompt' && inputs.length > 0 && (
            <div className='mt-4 flex flex-col gap-3'>
              {inputs.map((field, i) => (
                <div key={i}>
                  {field.label && <label className='mb-1 block text-sm font-medium text-gray-700'>{field.label}</label>}
                  <input
                    type='text'
                    value={inputValues[i] ?? ''}
                    onChange={(e) => setInputValue(i, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAction()}
                    placeholder={field.placeholder}
                    className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 outline-none ${inputErrors[i] ? 'border-red-400 focus:border-red-400' : 'border-gray-300 focus:border-blue-400'}`}
                    autoFocus={i === 0}
                  />
                  {inputErrors[i] && <p className='mt-1 text-xs text-red-500'>{inputErrors[i]}</p>}
                </div>
              ))}
            </div>
          )}
          <div className='mt-8 flex justify-end gap-3'>
            <CustomAlertDialogAction
              className='flex h-10 min-w-31 cursor-pointer items-center justify-center rounded-lg bg-blue-400 font-semibold text-white'
              onClick={handleAction}>
              {confirmText}
            </CustomAlertDialogAction>
            {(type === 'confirm' || type === 'prompt') && (
              <CustomAlertDialogCancel
                className='flex h-10 min-w-31 cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white'
                onClick={handleCancel}>
                {cancelText}
              </CustomAlertDialogCancel>
            )}
          </div>
        </div>
      </CustomAlertDialogContent>
    </CustomAlertDialog>
  );
}
